import type { Room } from '@hollowveil/shared';

/**
 * La Salle de la Révélation - Scène Finale
 *
 * Après avoir aidé tous les personnages des 5 mondes,
 * Côtelette est convoquée ici par l'Oracle Chevelu pour
 * une "grande révélation".
 *
 * C'est ici que la perruque tombe et que Benours se révèle,
 * avec tous les personnages aidés qui apparaissent pour
 * la remercier.
 */
export const roomFinale: Room = {
  id: 'room_finale',
  name: 'La Salle de la Révélation',
  description:
    'Une salle majestueuse baignée de lumière dorée. Tous ceux que tu as aidés sont rassemblés ici.',
  size: [24, 10, 24],
  palette: 'golden',
  floorStyle: 'marble',
  initialWorld: 'real',
  hasSpectralLayer: true,
  ambiance: {
    realWorld: {
      backgroundColor: '#2d1b4e',
      ambientLightColor: '#ffd700',
      ambientLightIntensity: 0.8,
      fogColor: '#2d1b4e',
      fogDensity: 0.01,
    },
    spectralWorld: {
      backgroundColor: '#1a0a2e',
      ambientLightColor: '#ff69b4',
      ambientLightIntensity: 0.6,
      fogColor: '#1a0a2e',
      fogDensity: 0.015,
    },
  },
  objects: [
    // Scène centrale surélevée
    {
      id: 'obj_main_stage',
      name: 'Scène Centrale',
      type: 'static',
      modelId: 'platform_gold',
      position: [12, 0, 12],
      rotation: [0, 0, 0],
      scale: [6, 0.5, 6],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },
    // Trône de l'Oracle (d'où il fait sa révélation)
    {
      id: 'obj_oracle_throne',
      name: "Trône de l'Oracle",
      type: 'static',
      modelId: 'throne_mystical',
      position: [12, 0.5, 9],
      rotation: [0, Math.PI, 0],
      scale: [1.5, 2, 1.5],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },
    // L'Oracle Chevelu / Benours - Position initiale avant révélation
    {
      id: 'obj_oracle_finale',
      name: "L'Oracle Chevelu",
      type: 'interactive',
      modelId: 'npc_oracle_chevelu',
      position: [12, 0.8, 9],
      rotation: [0, Math.PI, 0],
      scale: [1, 1, 1],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {
        isNPC: true,
        dialogueId: 'oracle_finale_reveal',
        isFinalScene: true,
      },
      onInteract: [
        {
          type: 'showRiddle',
          params: {
            riddleId: 'finale_revelation',
          },
        },
      ],
    },
    // Perruque tombée (visible après la révélation - en spectral initialement)
    {
      id: 'obj_fallen_wig',
      name: 'Perruque Majestueuse (tombée)',
      type: 'static',
      modelId: 'wig_magnificent',
      position: [13, 0.5, 10],
      rotation: [Math.PI / 6, 0.3, 0],
      scale: [0.8, 0.8, 0.8],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {
        description: 'La magnifique perruque de l\'Oracle... par terre.',
      },
    },
    // Gradins pour les personnages sauvés - Côté gauche
    {
      id: 'obj_stands_left',
      name: 'Gradins Gauche',
      type: 'static',
      modelId: 'wooden_stands',
      position: [5, 0, 12],
      rotation: [0, Math.PI / 2, 0],
      scale: [4, 1.5, 2],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },
    // Gradins pour les personnages sauvés - Côté droit
    {
      id: 'obj_stands_right',
      name: 'Gradins Droit',
      type: 'static',
      modelId: 'wooden_stands',
      position: [19, 0, 12],
      rotation: [0, -Math.PI / 2, 0],
      scale: [4, 1.5, 2],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },
    // Le Chapelier Fou - Sauvé de Wonderland
    {
      id: 'obj_mad_hatter_finale',
      name: 'Le Chapelier Fou',
      type: 'interactive',
      modelId: 'npc_mad_hatter',
      position: [4, 1.5, 10],
      rotation: [0, Math.PI / 4, 0],
      scale: [0.9, 0.9, 0.9],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {
        isNPC: true,
        dialogueId: 'hatter_thanks',
        savedFrom: 'wonderland',
      },
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message:
              'Le Chapelier: "Grâce à toi, j\'ai retrouvé mon calme intérieur. Chaque tasse de thé est maintenant une célébration!"',
          },
        },
      ],
    },
    // Le Lièvre de Mars - Sauvé de Wonderland
    {
      id: 'obj_march_hare_finale',
      name: 'Le Lièvre de Mars',
      type: 'interactive',
      modelId: 'npc_march_hare',
      position: [4, 1.5, 12],
      rotation: [0, Math.PI / 3, 0],
      scale: [0.85, 0.85, 0.85],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {
        isNPC: true,
        dialogueId: 'hare_thanks',
        savedFrom: 'wonderland',
      },
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message:
              'Le Lièvre: "Tu m\'as appris que c\'est OK de prendre son temps. Même pour un lièvre!"',
          },
        },
      ],
    },
    // Vékeuna (Eleven) - Sauvée de Hawkins
    {
      id: 'obj_vekeuna_finale',
      name: 'Vékeuna',
      type: 'interactive',
      modelId: 'npc_eleven',
      position: [4, 1.5, 14],
      rotation: [0, Math.PI / 5, 0],
      scale: [0.9, 0.9, 0.9],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {
        isNPC: true,
        dialogueId: 'eleven_thanks',
        savedFrom: 'hawkins',
      },
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message:
              'Vékeuna: "Tu m\'as montré que mes pouvoirs ne me définissent pas. Je suis plus que ça. Friends don\'t lie."',
          },
        },
      ],
    },
    // Blue (Raptor) - Sauvée de Jurassic Park
    {
      id: 'obj_blue_finale',
      name: 'Blue',
      type: 'interactive',
      modelId: 'npc_raptor_blue',
      position: [20, 1.5, 10],
      rotation: [0, -Math.PI / 4, 0],
      scale: [1, 1, 1],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {
        isNPC: true,
        dialogueId: 'blue_thanks',
        savedFrom: 'jurassic',
      },
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message:
              'Blue: *fait un son affectueux* (Elle semble te remercier de lui avoir fait confiance)',
          },
        },
      ],
    },
    // Bébé Raptor - Le compagnon gagné
    {
      id: 'obj_baby_raptor_finale',
      name: 'Bébé Raptor',
      type: 'interactive',
      modelId: 'npc_baby_raptor',
      position: [21, 1.5, 11],
      rotation: [0, -Math.PI / 3, 0],
      scale: [0.5, 0.5, 0.5],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {
        isNPC: true,
        dialogueId: 'baby_raptor_happy',
        isCompanion: true,
      },
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message:
              'Le bébé raptor te regarde avec adoration. Il a trouvé sa famille en toi.',
          },
        },
      ],
    },
    // Tyrion Lannister - Sauvé de Winterfell
    {
      id: 'obj_tyrion_finale',
      name: 'Tyrion Lannister',
      type: 'interactive',
      modelId: 'npc_tyrion',
      position: [20, 1.5, 12],
      rotation: [0, -Math.PI / 4, 0],
      scale: [0.7, 0.7, 0.7],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {
        isNPC: true,
        dialogueId: 'tyrion_thanks',
        savedFrom: 'winterfell',
      },
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message:
              'Tyrion: "Tu m\'as rappelé que ma valeur ne dépend pas du regard des autres. Je bois à ta santé!"',
          },
        },
      ],
    },
    // Eärwen - Sauvée de la Terre du Milieu
    {
      id: 'obj_earwen_finale',
      name: 'Eärwen',
      type: 'interactive',
      modelId: 'npc_young_elf',
      position: [20, 1.5, 14],
      rotation: [0, -Math.PI / 5, 0],
      scale: [0.95, 0.95, 0.95],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {
        isNPC: true,
        dialogueId: 'earwen_thanks',
        savedFrom: 'middle_earth',
      },
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message:
              'Eärwen: "Tu m\'as montré que ma façon d\'apprendre est unique, pas déficiente. Hannon le, mellon."',
          },
        },
      ],
    },
    // Tapis rouge vers la scène
    {
      id: 'obj_red_carpet',
      name: 'Tapis Rouge',
      type: 'static',
      modelId: 'carpet_royal',
      position: [12, 0.01, 18],
      rotation: [0, 0, 0],
      scale: [2, 0.02, 6],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: { color: '#8b0000' },
    },
    // Colonnes décoratives
    {
      id: 'obj_pillar_1',
      name: 'Colonne Dorée',
      type: 'static',
      modelId: 'pillar_gold',
      position: [6, 0, 6],
      rotation: [0, 0, 0],
      scale: [0.8, 4, 0.8],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },
    {
      id: 'obj_pillar_2',
      name: 'Colonne Dorée',
      type: 'static',
      modelId: 'pillar_gold',
      position: [18, 0, 6],
      rotation: [0, 0, 0],
      scale: [0.8, 4, 0.8],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },
    {
      id: 'obj_pillar_3',
      name: 'Colonne Dorée',
      type: 'static',
      modelId: 'pillar_gold',
      position: [6, 0, 18],
      rotation: [0, 0, 0],
      scale: [0.8, 4, 0.8],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },
    {
      id: 'obj_pillar_4',
      name: 'Colonne Dorée',
      type: 'static',
      modelId: 'pillar_gold',
      position: [18, 0, 18],
      rotation: [0, 0, 0],
      scale: [0.8, 4, 0.8],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },
    // Coeur géant en spectral (symbolise l'amour de Benours)
    {
      id: 'obj_giant_heart',
      name: 'Coeur de Lumière',
      type: 'static',
      modelId: 'heart_crystal',
      position: [12, 5, 9],
      rotation: [0, 0, 0],
      scale: [2, 2, 1],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {
        glows: true,
        color: '#ff69b4',
        description: "L'amour véritable de celui qui a créé tout cela pour toi.",
      },
    },
    // Message caché en spectral
    {
      id: 'obj_love_message',
      name: 'Message Flottant',
      type: 'interactive',
      modelId: 'floating_text',
      position: [12, 6, 12],
      rotation: [0, 0, 0],
      scale: [3, 1, 1],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {
        text: 'Je t\'aime, Côtelette',
        glows: true,
        color: '#ffd700',
      },
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message:
              'Des lettres de lumière flottent dans l\'air: "Je t\'aime, Côtelette - Ton Benours"',
          },
        },
      ],
    },
    // Confettis / Pétales (décoratifs)
    {
      id: 'obj_confetti_1',
      name: 'Pétales',
      type: 'static',
      modelId: 'particles_petals',
      position: [12, 3, 12],
      rotation: [0, 0, 0],
      scale: [8, 4, 8],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: { animated: true, color: '#ffb6c1' },
    },
    // Tous les animaux pettables des différents mondes réunis
    // Chat du Cheshire - de Wonderland
    {
      id: 'obj_cheshire_finale',
      name: 'Chat du Cheshire',
      type: 'static',
      modelId: 'cat_cheshire',
      position: [8, 0.5, 8],
      rotation: [0, Math.PI / 6, 0],
      scale: [0.8, 0.8, 0.8],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: { floats: true },
    },
    // Firefly - de Hawkins
    {
      id: 'obj_firefly_finale',
      name: 'Luciole',
      type: 'static',
      modelId: 'firefly',
      position: [16, 2, 8],
      rotation: [0, 0, 0],
      scale: [0.3, 0.3, 0.3],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: { glows: true },
    },
  ],
  puzzles: [
    // Puzzle final - Parler à l'Oracle pour déclencher la révélation
    {
      id: 'puzzle_finale_revelation',
      name: 'La Grande Révélation',
      type: 'dialogue',
      state: 'active',
      description: "L'Oracle veut te révéler quelque chose d'important...",
      hint: "Parle à l'Oracle Chevelu sur son trône.",
      requiredWorld: 'real',
      linkedObjectIds: ['obj_oracle_finale'],
      solution: {
        type: 'dialogue',
        expected: { completed: true },
      },
      onSolve: [
        {
          type: 'showMessage',
          params: {
            message:
              "La perruque tombe... révélant Benours ! 'Surprise, mon amour ! C'est moi qui ai créé tout ça pour toi !'",
          },
        },
        {
          type: 'triggerEvent',
          params: { eventId: 'finale_wig_falls' },
        },
      ],
    },
  ],
  connections: [
    {
      targetRoomId: 'room_sanctuary',
      position: [12, 0, 22],
      direction: 'south',
      locked: false,
    },
  ],
  npcs: [
    // Papillon doré - compagnon de célébration
    {
      id: 'npc_golden_butterfly',
      type: 'butterfly',
      name: 'Papillon Doré',
      position: [10, 2, 15],
      scale: 1.2,
      idleAnimation: 'fly',
      petAnimation: 'sparkle',
      petRadius: 2,
      petCooldown: 2000,
      isPettable: true,
      visibleInWorld: 'both',
    },
    // Colombe de paix
    {
      id: 'npc_dove',
      type: 'bird',
      name: 'Colombe',
      position: [14, 3, 15],
      scale: 0.8,
      idleAnimation: 'fly',
      petAnimation: 'coo',
      petRadius: 2.5,
      petCooldown: 2500,
      isPettable: true,
      visibleInWorld: 'real',
    },
    // Phoenix miniature (spectral) - symbolise la renaissance
    {
      id: 'npc_mini_phoenix',
      type: 'firefly',
      name: 'Petit Phoenix',
      position: [12, 4, 10],
      scale: 1.5,
      idleAnimation: 'fly',
      petAnimation: 'flame',
      petRadius: 3,
      petCooldown: 3000,
      isPettable: true,
      visibleInWorld: 'spectral',
    },
  ],
};
